import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { DataLoaderComponent } from '../../shared/data-loader/data-loader.component';
import { SnackbarService } from '../../services/snackbar.service';
import { StationScheduleService } from '../../services/station-schedule.service';
import { CartService } from '../../services/cart.service';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { PermissionService } from '../../services/permission.service';
import { environment } from '../../../environments/environment';
import {
  linkSessionPaidAmountToOrder,
  saveOrderPaidAmount,
  saveSessionPaidAmount,
} from '../../utils/order-display-amount';
import { isFulfilmentRejected } from '../../utils/payment-errors';

@Component({
  selector: 'app-payment-success',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatDividerModule,
    DataLoaderComponent,
  ],
  templateUrl: './payment-success.component.html',
  styleUrl: './payment-success.component.css',
})
export class PaymentSuccessComponent implements OnInit, OnDestroy {
  loadingSession = false;
  /** True when backend confirms SUCCEEDED */
  confirmedSuccess = false;
  /** True while polling for SUCCEEDED / order_id from backend */
  processingUi = false;
  /** Show "Creating…" for Order ID until backend assigns order_id */
  awaitingOrderId = false;
  orderSummary: { orderId?: number; amount?: number; currency?: string; status?: string } | null = null;
  /** One row per campaign when this payment checked out a cart. */
  orders: Array<{
    id: number
    total_cost: number | null
    station_name?: string | null
    call_sign?: string | null
  }> = [];
  loadingOrders = false;
  ordersPending = 0;
  /** progress: none yet. partial: some orders, more coming. complete: done. stopped: retries ended. */
  fulfillmentPhase: 'progress' | 'partial' | 'complete' | 'stopped' = 'progress';
  cartCheckout = false;
  stoppedMessage = '';
  isFreeOrder = false;
  isDownloadingReceipt = false;
  downloadingOrderId: number | null = null;

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private sessionId: number | null = null;
  private pollCount = 0;
  private pollsExhausted = false;
  private cartSynced = false;
  private readonly MAX_POLLS = 8;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private scheduleService: StationScheduleService,
    private snackbar: SnackbarService,
    private apiService: ApiService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private cartService: CartService,
  ) {}

  /**
   * "Am I station staff anywhere?" — derived from the assignment list, not the
   * global users.role column. That column holds the role of the first station the
   * account was ever added to and is never refreshed, so an advertiser who is
   * later given a station assignment kept being shown the advertiser-only view.
   * Intentionally station-agnostic: this page is not scoped to one station.
   */
  get isRegularUser(): boolean {
    return this.permissionService.isAdvertiserUserOnly();
  }

  downloadReceipt(orderId: number): void {
    this.isDownloadingReceipt = true;
    this.downloadingOrderId = orderId;
    this.apiService
      .authenticatedGet<{ pdf_base64: string; filename?: string }>(
        `${environment.apiUrl}/orders/${orderId}/receipt`,
      )
      .subscribe({
        next: (response) => {
          const byteCharacters = atob(response.pdf_base64);
          const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0));
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = response.filename || `RAD-Receipt-${orderId}.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
          this.isDownloadingReceipt = false;
          this.downloadingOrderId = null;
        },
        error: (err) => {
          console.error('Receipt download failed', err);
          this.isDownloadingReceipt = false;
          this.downloadingOrderId = null;
          this.snackbar.error('Failed to download receipt. Please try again.');
        },
      });
  }

  ngOnInit() {
    const qs = this.route.snapshot.queryParamMap;
    this.isFreeOrder =
      qs.get('free') === 'true' || qs.get('is_free_order') === 'true';

    // Handle Stripe redirect path (bank transfers, 3DS) that land directly here
    if (qs.get('redirect_status') === 'failed') {
      const sid = qs.get('session_id');
      this.router.navigate(['/payment-failure'], {
        queryParams: sid ? { session_id: sid } : {}
      });
      return;
    }

    const orderIdFromUrl = qs.get('order_id');
    const paymentIntentId = qs.get('payment_intent');
    const sessionIdStr =
      qs.get('session_id') || localStorage.getItem('activePaymentSessionId');

    const prefOrderId = orderIdFromUrl ? parseInt(orderIdFromUrl, 10) : undefined;

    if (sessionIdStr) {
      const sid = parseInt(sessionIdStr, 10);
      if (!isNaN(sid)) {
        this.sessionId = sid;
        this.confirmedSuccess = true;
        this.loadingOrders = true;
        this.orderSummary = { status: 'Paid', currency: 'USD' };
        this.pollCount = 0;
        this.clearCheckoutStorage();
        this.loadSessionDetails(sid, prefOrderId);
        return;
      }
    }

    if (paymentIntentId) {
      this.confirmedSuccess = true;
      this.loadingOrders = true;
      this.orderSummary = { status: 'Paid', currency: 'USD' };
      this.pollCount = 0;
      this.scheduleService.getPaymentSessionByPaymentIntent(paymentIntentId).subscribe({
        next: (session) => {
          this.clearCheckoutStorage();
          this.sessionId = session.id;
          this.loadSessionDetails(session.id, prefOrderId ?? session.order_id ?? undefined);
        },
        error: () => {
          this.clearCheckoutStorage();
          this.loadingSession = false;
          this.loadingOrders = false;
          this.confirmedSuccess = true;
          this.processingUi = false;
          this.awaitingOrderId = false;
          this.fulfillmentPhase = 'stopped';
          this.stoppedMessage =
            'Payment received, but the order details could not be loaded. They will appear in My Orders shortly.';
        },
      });
      return;
    }

    this.clearCheckoutStorage();

    if (orderIdFromUrl && !isNaN(prefOrderId!)) {
      this.confirmedSuccess = true;
      this.processingUi = false;
      this.fulfillmentPhase = 'complete';
      this.orderSummary = {
        orderId: prefOrderId,
        amount: this.isFreeOrder ? 0 : undefined,
        status: this.isFreeOrder ? 'Free order' : 'Confirmed',
      };
    } else {
      this.confirmedSuccess = true;
      this.processingUi = false;
      this.fulfillmentPhase = 'stopped';
      this.stoppedMessage = 'Payment received. Check My Orders for your campaign.';
    }
  }

  refreshSession() {
    if (this.sessionId == null) return;
    this.clearPollTimer();
    this.pollCount = 0;
    this.loadingOrders = true;
    this.loadSessionDetails(this.sessionId, this.orderSummary?.orderId);
  }

  private clearPollTimer() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private loadSessionDetails(sessionId: number, prefOrderId?: number) {
    this.clearPollTimer();
    this.scheduleService.getPaymentSession(sessionId).subscribe({
      next: (session) => {
        if (this.destroyed) return;
        this.loadingSession = false;

        // Terminal failure — redirect away
        if (isFulfilmentRejected(session)) {
          this.showFulfilmentRejected(sessionId);
          return;
        }
        if (
          session.status === 'FAILED' ||
          session.status === 'CANCELLED' ||
          session.status === 'EXPIRED'
        ) {
          this.snackbar.error('This payment did not complete.');
          this.router.navigate(['/payment-failure'], { queryParams: { session_id: sessionId } });
          return;
        }

        this.applySessionToUi(session, sessionId, prefOrderId);
        this.continuePolling(session, sessionId, prefOrderId);
      },
      error: () => {
        if (this.destroyed) return;
        this.loadingSession = false;
        // Even on HTTP error, show positive UI since Stripe already confirmed.
        // If the session load fails it's a network issue, not a payment issue.
        this.confirmedSuccess = true;
        this.processingUi = false;
        if (!this.orderSummary) {
          this.orderSummary = { status: 'Paid' };
        }
        if (this.sessionId != null && !this.pollsExhausted) {
          this.schedulePoll(this.sessionId, this.orderSummary?.orderId);
        } else {
          this.stopPolling();
        }
      }
    });
  }

  private continuePolling(
    session: { status: string },
    sessionId: number,
    prefOrderId?: number,
  ): void {
    if (
      session.status === 'FAILED' ||
      session.status === 'CANCELLED' ||
      session.status === 'EXPIRED'
    ) {
      return;
    }
    if (this.fulfillmentPhase === 'complete' || this.pollsExhausted) return;
    this.schedulePoll(sessionId, prefOrderId);
  }

  stationLabel(order: { station_name?: string | null; call_sign?: string | null }): string {
    const name = order.station_name?.trim();
    const call = order.call_sign?.trim();
    if (name && call) return `${name} (${call})`;
    return name || call || 'Campaign';
  }

  private applySessionToUi(
    session: {
      status: string
      order_id?: number
      amount?: number
      currency?: string
      campaign_config?: { checkout_kind?: string; items?: unknown[] }
      orders?: Array<{
        id: number
        total_cost: number | null
        station_name?: string | null
        call_sign?: string | null
      }>
      orders_pending?: number
      cart_item_count?: number
    },
    sessionId: number,
    prefOrderId?: number,
  ): void {
    this.orders = Array.isArray(session.orders) ? session.orders.filter((order) => order?.id) : [];
    const kind = session.campaign_config?.checkout_kind;
    if (kind === 'cart' || (session.cart_item_count ?? 0) > 1 || this.orders.length > 1) {
      this.cartCheckout = true;
    }
    const knownId = prefOrderId ?? session.order_id;
    if (this.cartCheckout && this.orders.length === 0 && knownId) {
      this.orders = [{ id: knownId, total_cost: null }];
    }
    const configCount = kind === 'cart' && Array.isArray(session.campaign_config?.items)
      ? session.campaign_config.items.length
      : 0;
    const reportedPending = session.orders_pending ?? 0;
    const expected = session.cart_item_count || configCount;
    const missing = this.cartCheckout && expected > this.orders.length ? expected - this.orders.length : 0;
    this.ordersPending = Math.max(reportedPending, missing);
    const orderId = this.cartCheckout ? undefined : (prefOrderId ?? session.order_id ?? this.orders[0]?.id);
    this.confirmedSuccess = true;
    this.processingUi = false;
    this.awaitingOrderId = !this.cartCheckout && !orderId && this.orders.length === 0;
    this.orderSummary = {
      orderId,
      amount: session.amount,
      currency: session.currency || 'USD',
      status: session.status === 'SUCCEEDED' || this.orders.length > 0 ? 'Paid' : 'Confirming',
    };
    this.updateFulfillmentPhase();
    this.syncCartAfterPayment();
    this.persistPaidFromSession(sessionId, session, prefOrderId);
  }

  private updateFulfillmentPhase(): void {
    const ready = this.orders.length > 0 || (!this.cartCheckout && this.orderSummary?.orderId != null);
    const waiting = this.ordersPending > 0 || !ready;
    if (!waiting) {
      this.fulfillmentPhase = 'complete';
      this.loadingOrders = false;
      this.awaitingOrderId = false;
      return;
    }
    if (this.pollsExhausted) {
      this.fulfillmentPhase = 'stopped';
      this.loadingOrders = false;
      this.awaitingOrderId = false;
      this.stoppedMessage = this.orders.length > 0
        ? `Payment received. ${this.orders.length} ${this.orders.length === 1 ? 'order is' : 'orders are'} ready. Any campaign still processing will show in My Orders.`
        : 'Payment received. Your orders are still being created. They will appear in My Orders shortly.';
      return;
    }
    this.fulfillmentPhase = this.orders.length > 0 ? 'partial' : 'progress';
    this.loadingOrders = this.fulfillmentPhase === 'progress';
  }

  private syncCartAfterPayment(): void {
    if (!this.cartCheckout) return;
    if (!this.cartSynced) {
      this.cartSynced = true;
      this.cartService.acknowledgeCheckout();
    }
    if (this.fulfillmentPhase === 'complete') {
      this.cartService.refreshCount();
    }
  }

  private stopPolling(): void {
    this.pollsExhausted = true;
    this.clearPollTimer();
    this.confirmedSuccess = true;
    this.processingUi = false;
    if (!this.orderSummary) {
      this.orderSummary = { status: 'Paid', currency: 'USD' };
    }
    this.orderSummary = { ...this.orderSummary, status: 'Paid' };
    this.updateFulfillmentPhase();
  }

  /**
   * Poll until every campaign has an order. Stops after MAX_POLLS so the
   * progress state cannot spin forever.
   */
  private schedulePoll(sessionId: number, prefOrderId?: number) {
    this.clearPollTimer();
    if (this.pollsExhausted || this.fulfillmentPhase === 'complete') return;
    if (this.pollCount >= this.MAX_POLLS) {
      this.stopPolling();
      return;
    }

    const delay = this.pollCount < 3 ? 2000 :
                  this.pollCount < 5 ? 3000 : 4000;

    this.pollTimer = setTimeout(() => {
      if (this.destroyed) return;
      this.pollCount++;
      this.scheduleService.getPaymentSession(sessionId).subscribe({
        next: (session) => {
          if (this.destroyed) return;
          if (isFulfilmentRejected(session)) {
            this.showFulfilmentRejected(sessionId);
            return;
          }
          if (
            session.status === 'FAILED' ||
            session.status === 'CANCELLED' ||
            session.status === 'EXPIRED'
          ) {
            this.router.navigate(['/payment-failure'], { queryParams: { session_id: sessionId } });
            return;
          }
          this.applySessionToUi(session, sessionId, prefOrderId);
          this.continuePolling(session, sessionId, prefOrderId);
        },
        error: () => {
          if (this.destroyed) return;
          this.schedulePoll(sessionId, prefOrderId);
        }
      });
    }, delay);
  }

  private persistPaidFromSession(
    sessionId: number,
    session: { order_id?: number; amount?: number },
    prefOrderId?: number,
  ): void {
    const amount = this.orderSummary?.amount ?? session.amount;
    if (amount == null || !Number.isFinite(amount)) {
      return;
    }
    saveSessionPaidAmount(sessionId, amount);
    if (this.cartCheckout) {
      for (const order of this.orders) {
        if (order.total_cost != null) saveOrderPaidAmount(order.id, order.total_cost);
      }
      return;
    }
    const orderId = prefOrderId ?? session.order_id ?? this.orders[0]?.id;
    if (orderId != null) {
      linkSessionPaidAmountToOrder(sessionId, orderId);
      saveOrderPaidAmount(orderId, this.orders[0]?.total_cost ?? amount);
    }
  }

  formatMoney(amount: number, currency: string) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase()
      }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  }

  goToOrders() {
    this.router.navigate(['/orders'], { state: { fromPaymentSuccess: true } });
  }

  private clearCheckoutStorage() {
    localStorage.removeItem('activePaymentSessionId');
    localStorage.removeItem('campaignBuilderState');
    localStorage.removeItem('campaignPreview');
    localStorage.removeItem('campaignRequest');
    localStorage.removeItem('campaignPreviewDetails');
    localStorage.removeItem('wasInPreview');
  }

  /**
   * The payment was captured but the campaign couldn't be booked (failure_code
   * "fulfilment_rejected"): stop "processing" and show the refund state on the failure page,
   * which explains it and offers no retry.
   */
  private showFulfilmentRejected(sessionId: number): void {
    this.clearPollTimer();
    this.processingUi = false;
    this.awaitingOrderId = false;
    this.confirmedSuccess = false;
    this.router.navigate(['/payment-failure'], { queryParams: { session_id: sessionId } });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.clearPollTimer();
  }
}
